import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  selectErrorEvents,
  selectRetryingErrorId,
} from '../../state/aiSidekickChatSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { getLatestUserMessage } from '../../utils/get-latest-user-message';
import AgentErrorCard from '../AgentErrorCard';
import MessageBubble from '../MessageBubble';
import ThinkingIndicator from '../ThinkingIndicator';
import useStyles from './MessageList.styles';
import { buildMessageTimeline } from './timeline';
import type { MessageListProps } from './types';
import useAutoscroll from './useAutoscroll';

export type { MessageListProps } from './types';

/** Scrollable chat message list with bottom-pinned autoscroll. */
function MessageList({
  isM1Enabled = false,
  messages,
  isStreaming = false,
  thinkingAttempt,
  thinkingMaxAttempts,
  currentToolActivity = null,
  isReconnecting = false,
  onRetry,
  editControls,
  findArtifact,
  trailingSlot,
  className,
}: MessageListProps) {
  const { classes, cx } = useStyles({ isM1Enabled });
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const {
    scrollContainerRef,
    endAnchorRef,
    scrollToBottomIfPinned,
    scrollToBottom,
    scrollElementToTop,
  } = useAutoscroll(isStreaming);
  const errorEvents = useSelector(selectErrorEvents);
  const retryingErrorId = useSelector(selectRetryingErrorId);
  const errorEventCount = errorEvents.length;
  const latestMessageElementRef = useRef<HTMLDivElement | null>(null);
  const previousMessageCountRef = useRef(messages.length);
  const previousHasTrailingSlotRef = useRef(false);
  const previousErrorEventCountRef = useRef(errorEventCount);
  const previousThinkingAttemptRef = useRef(thinkingAttempt);
  const hasMountedRef = useRef(false);
  const showThinkingIndicator = isStreaming;
  // Detect presence via `!= null` rather than `Boolean(...)` so legal
  // ReactNode render values that coerce to false (most notably `0`,
  // which renders the literal "0") still trigger the autoscroll
  // effect when they mount.
  const hasTrailingSlot = trailingSlot != null;

  // Sort by `createdAt` so inline error cards interleave with the
  // surrounding user and assistant turns chronologically.
  const timeline = useMemo(
    () => buildMessageTimeline(messages, errorEvents),
    [messages, errorEvents]
  );

  useLayoutEffect(() => {
    const didAppendMessage = messages.length > previousMessageCountRef.current;
    const didMountTrailingSlot =
      hasTrailingSlot && !previousHasTrailingSlotRef.current;
    const didAppendErrorEvent =
      errorEventCount > previousErrorEventCountRef.current;
    const didChangeThinkingAttempt =
      thinkingAttempt !== previousThinkingAttemptRef.current;
    const latestMessage = messages[messages.length - 1];

    if (!hasMountedRef.current) {
      scrollToBottomIfPinned();
      hasMountedRef.current = true;
    } else if (didMountTrailingSlot) {
      scrollToBottom();
    } else if (didAppendMessage && latestMessage?.role !== 'user') {
      scrollElementToTop(latestMessageElementRef.current);
    } else if (
      didAppendMessage ||
      didAppendErrorEvent ||
      didChangeThinkingAttempt ||
      isStreaming
    ) {
      scrollToBottomIfPinned();
    }

    previousMessageCountRef.current = messages.length;
    previousHasTrailingSlotRef.current = hasTrailingSlot;
    previousErrorEventCountRef.current = errorEventCount;
    previousThinkingAttemptRef.current = thinkingAttempt;
  }, [
    errorEventCount,
    hasTrailingSlot,
    isStreaming,
    messages,
    scrollElementToTop,
    scrollToBottom,
    scrollToBottomIfPinned,
    thinkingAttempt,
  ]);

  const latestMessageId = messages[messages.length - 1]?.id;
  const latestUserMessageId = getLatestUserMessage(messages)?.id;
  const branchNavigation = editControls?.branchNavigation;
  const branchAnnouncement =
    branchNavigation === null || branchNavigation === undefined
      ? ''
      : t('chat.branch.switchAnnouncement', {
          current: branchNavigation.current,
          total: branchNavigation.total,
        });

  return (
    <div
      ref={scrollContainerRef}
      className={cx(classes.scrollContainer, className)}
      data-testid="ai-sidekick-message-list"
    >
      <div className={classes.list}>
        {timeline.map((item) => {
          if (item.kind === 'message') {
            const userControls =
              isM1Enabled &&
              editControls !== undefined &&
              item.data.id === latestUserMessageId
                ? {
                    isEditing: editControls.editingMessageId === item.data.id,
                    isBusy: editControls.isBusy,
                    isCreatingBranch: editControls.isCreatingBranch,
                    branchNavigation: editControls.branchNavigation,
                    onEdit: () => editControls.onEdit(item.data.id),
                    onCancelEdit: editControls.onCancelEdit,
                    onSubmitEdit: (content: string) =>
                      editControls.onSubmitEdit(item.data.id, content),
                    onSwitchBranch: editControls.onSwitchBranch,
                  }
                : undefined;
            return (
              <MessageBubble
                key={item.data.id}
                ref={
                  item.data.id === latestMessageId
                    ? latestMessageElementRef
                    : undefined
                }
                message={item.data}
                isM1Enabled={isM1Enabled}
                userControls={userControls}
                findArtifact={findArtifact}
              />
            );
          }
          return (
            <AgentErrorCard
              key={item.data.id}
              errorId={item.data.id}
              variant={item.data.variant}
              canRetry={item.data.canRetry}
              retryingErrorId={retryingErrorId}
              isStreaming={isStreaming}
              onRetry={onRetry}
              sourceUserMessageId={item.data.sourceUserMessageId}
            />
          );
        })}
        {showThinkingIndicator ? (
          <div className={classes.thinkingSlot}>
            <ThinkingIndicator
              attempt={thinkingAttempt}
              maxAttempts={thinkingMaxAttempts}
              toolActivity={currentToolActivity}
              isReconnecting={isReconnecting}
            />
          </div>
        ) : null}
        {trailingSlot}
      </div>
      {isM1Enabled ? (
        <span
          className={classes.visuallyHidden}
          aria-live="polite"
          aria-atomic="true"
          data-testid="ai-sidekick-branch-announcement"
        >
          {branchAnnouncement}
        </span>
      ) : null}
      <div
        ref={endAnchorRef}
        className={classes.endAnchor}
        data-testid="ai-sidekick-message-list-end"
      />
    </div>
  );
}

MessageList.displayName = 'MessageList';

export default memo(MessageList);
